import type { ProformaInfo, ProformaLine, ProformaCurrency, ProformaWindow } from '@/types/ProformaTypes';

export function mapZeroValuesToNull<T>(arr: T[]) {
  const result: T[] = [];
  arr.forEach((obj) => {
    const newObj = obj as any;
    Object.entries(newObj).forEach(([key, value]) => {
      if (typeof value === 'number' && value === 0) {
        newObj[key] = undefined;
      }
    });
    result.push(newObj);
  });
  return result;
}

export function getSubTotalValue(
  quantity: ProformaLine['QUANTITY_IN'],
  unitPrice: ProformaLine['RATE'],
  multiplier: ProformaLine['MULTIPLE']
): number | undefined {
  if (!quantity && !multiplier && !unitPrice) {
    return undefined;
  }
  const qty = quantity ?? 0;
  const price = unitPrice ?? 0;
  return qty * price * (multiplier ?? 1);
}

export function getRebateAmountFromRebatePercent(
  quantity: ProformaLine['QUANTITY_IN'],
  unitPrice: ProformaLine['RATE'],
  multiplier: ProformaLine['MULTIPLE'],
  rebateAmount: ProformaLine['REBATE_AMOUNT'],
  rebatePercent: ProformaLine['REBATE_IN']
): number | undefined {
  if (!rebatePercent) {
    if (rebateAmount) {
      return rebateAmount;
    }
    return undefined;
  }
  const asPercent = Number(rebatePercent);
  if (Number.isNaN(asPercent)) {
    return undefined;
  }
  const subtotal = getSubTotalValue(quantity, unitPrice, multiplier);
  if (!subtotal && subtotal !== 0) {
    return undefined;
  }
  return parseFloat(((rebatePercent * subtotal) / 100).toFixed(2)); // Round to 2 decimal places because the user-input is forced to be 2 decimal places
}

export function getVatAmount(
  quantity: ProformaLine['QUANTITY_IN'],
  rate: ProformaLine['RATE'],
  multiplier: ProformaLine['MULTIPLE'],
  vatPercent: ProformaLine['VAT_PERCENT'],
  rebateAmount: ProformaLine['REBATE_AMOUNT']
): number | undefined {
  if (typeof vatPercent !== 'number' || vatPercent === 0) {
    return undefined;
  }
  const subtotal = getSubTotalValue(quantity, rate, multiplier);
  if (typeof subtotal !== 'number') {
    return undefined;
  }
  const rebate = subtotal + (rebateAmount ?? 0);
  const vatAmount = (vatPercent * rebate) / 100;
  return vatAmount;
}

export function getTotalValue({
  quantity,
  unitPrice,
  multiplier,
  rebateAmount,
  rebatePercent,
  vatPercent,
}: {
  quantity: number | undefined;
  unitPrice: number | undefined;
  multiplier: number | undefined;
  rebateAmount: number | undefined;
  rebatePercent: number | undefined;
  vatPercent: number | undefined;
}): number | undefined {
  const subTotal = getSubTotalValue(quantity, unitPrice, multiplier);
  // eslint-disable-next-line no-underscore-dangle
  const calculatedRebateAmount = getRebateAmountFromRebatePercent(
    quantity,
    unitPrice,
    multiplier,
    rebateAmount,
    rebatePercent
  );
  const vatAmount = getVatAmount(quantity, unitPrice, multiplier, vatPercent, calculatedRebateAmount);
  if (typeof subTotal !== 'number') {
    return 0;
  }
  let result = subTotal;
  if (typeof calculatedRebateAmount === 'number') {
    result += calculatedRebateAmount;
  }
  if (typeof vatAmount === 'number') {
    result += vatAmount;
  }
  return result;
}

export function getExchangeRate(
  fromCurrencyName: string | undefined,
  toCurrencyName: string,
  currencies: ProformaCurrency[]
): number {
  if (typeof fromCurrencyName !== 'string' || typeof toCurrencyName !== 'string') {
    return 1;
  }
  // The field name have a CURR_ before the cur code therefore i remove it here to be able
  // to use the cur code to find it in the currency array with roe using the cur code

  // Cheking if the from currencie is in the exchange rate list
  // if its not the find fuction will return undefined there for i have added an default value of 1
  const roeToLocal = currencies.find((x) => x.CURRENCY_NAME === fromCurrencyName)?.RATE ?? 1;

  // Cheking if the to currencie is in the exchange rate list
  // if its not the find fuction will return undefined there for i have added an default value of 1
  const roeLocalToTarget = currencies.find((x) => x.CURRENCY_NAME === toCurrencyName)?.RATE ?? 1;

  return roeToLocal / roeLocalToTarget;
}

export function getValueInLocalCurrency(
  value: number,
  fromCurrencyName: string | undefined,
  localCurrency: string | undefined,
  currencies: ProformaCurrency[]
): number {
  if (!fromCurrencyName) {
    return value;
  }
  return value * getExchangeRate(fromCurrencyName, localCurrency ?? '', currencies);
}

export function getValueInCurrency(
  value: number,
  fromCurrencyName: string | undefined,
  toCurrencyName: string,
  currencies: ProformaCurrency[]
): number {
  const exchangeRate = getExchangeRate(fromCurrencyName, toCurrencyName, currencies);

  return value * exchangeRate;
}

export function updateProformaLineCurrencyField({
  proformaLineBaseAmount,
  proformaLineCurrencyCode,
  proformaInfo,
  currencies,
}: {
  proformaLineBaseAmount: ProformaLine['BASE_AMOUNT'];
  proformaLineCurrencyCode: ProformaLine['CURRENCY_CODE'];
  proformaInfo: ProformaInfo;
  currencies: ProformaCurrency[];
}): Pick<ProformaLine, 'CUR1' | 'CUR2' | 'CUR3' | 'CUR4'> {
  const proformaDetails = { ...proformaInfo };

  const isUsed: string[] = currencies.filter((i) => i.PER_SHOW === 1).map((i) => i.CURRENCY_NAME);
  for (let index = 0; index < 4; index++) {
    const currencyCode = isUsed[index];
    const proformaCurrencyField = `CUR_CODE${index + 1}`;
    if (
      proformaCurrencyField === 'CUR_CODE1' ||
      proformaCurrencyField === 'CUR_CODE2' ||
      proformaCurrencyField === 'CUR_CODE3' ||
      proformaCurrencyField === 'CUR_CODE4'
    ) {
      proformaDetails[proformaCurrencyField] = currencyCode;
    }
  }

  const CUR1 = proformaDetails.CUR_CODE1
    ? getValueInCurrency(proformaLineBaseAmount, proformaLineCurrencyCode, proformaDetails.CUR_CODE1, currencies)
    : null;
  const CUR2 = proformaDetails.CUR_CODE2
    ? getValueInCurrency(proformaLineBaseAmount, proformaLineCurrencyCode, proformaDetails.CUR_CODE2, currencies)
    : null;
  const CUR3 = proformaDetails.CUR_CODE3
    ? getValueInCurrency(proformaLineBaseAmount, proformaLineCurrencyCode, proformaDetails.CUR_CODE3, currencies)
    : null;
  const CUR4 = proformaDetails.CUR_CODE4
    ? getValueInCurrency(proformaLineBaseAmount, proformaLineCurrencyCode, proformaDetails.CUR_CODE4, currencies)
    : null;
  return {
    CUR1,
    CUR2,
    CUR3,
    CUR4,
  };
  /*
  return {
    CUR1: CUR1 === 0 ? null : CUR1,
    CUR2: CUR2 === 0 ? null : CUR2,
    CUR3: CUR3 === 0 ? null : CUR3,
    CUR4: CUR4 === 0 ? null : CUR4,
  };
  */
}

export function calculateProformaLine({
  quantity,
  unitPrice,
  multiplier,
  rebatePercent,
  rebateAmount,
  vatPercent,
  proformaLine,
  localCurrency,
  currencies,
  proformaInfo,
}: {
  quantity: number | undefined;
  unitPrice: number | undefined;
  multiplier: number | undefined;
  rebatePercent: number | undefined;
  rebateAmount: number | undefined;
  vatPercent: number | undefined;
  proformaLine: ProformaLine;
  localCurrency: string | undefined;
  currencies: ProformaCurrency[];
  proformaInfo: ProformaInfo;
}): ProformaLine {
  const calculatedRebateAmount: ProformaLine['REBATE_AMOUNT'] = getRebateAmountFromRebatePercent(
    quantity,
    unitPrice,
    multiplier,
    rebateAmount,
    rebatePercent
  );
  let calculatedVatAmount: ProformaLine['VAT'] = proformaLine.VAT;
  if (vatPercent) {
    calculatedVatAmount = getVatAmount(quantity, unitPrice, multiplier, vatPercent, calculatedRebateAmount);
  }
  let calculatedTotalInLocalCurrency =
    getTotalValue({
      quantity,
      unitPrice,
      multiplier,
      rebateAmount: calculatedRebateAmount,
      rebatePercent,
      vatPercent,
    }) ?? 0;
  calculatedTotalInLocalCurrency = getValueInLocalCurrency(
    calculatedTotalInLocalCurrency,
    proformaLine.CURRENCY_CODE,
    localCurrency,
    currencies
  );

  const baseAmount = (proformaLine.RATE ?? 0) * (quantity ?? 0) * (multiplier ?? 1);

  const { CUR1, CUR2, CUR3, CUR4 } = updateProformaLineCurrencyField({
    currencies,
    proformaInfo,
    proformaLineBaseAmount: baseAmount,
    proformaLineCurrencyCode: proformaLine.CURRENCY_CODE,
  });

  const result: ProformaLine = {
    ...proformaLine,
    QUANTITY_IN: quantity,
    RATE: unitPrice,
    MULTIPLE: multiplier,
    REBATE_AMOUNT: calculatedRebateAmount,
    VAT_PERCENT: vatPercent,
    REBATE_IN: rebatePercent,
    VAT: calculatedVatAmount,
    AMOUNT_IN: calculatedTotalInLocalCurrency,
    BASE_AMOUNT: baseAmount,
    CUR1,
    CUR2,
    CUR3,
    CUR4,
  };

  return result;
}

export function reCalculateProformaLines({
  proformaLines,
  currencies,
  proformaInfo,
}: {
  proformaLines: ProformaLine[];
  currencies: ProformaCurrency[];
  proformaInfo: ProformaInfo;
}) {
  const result: ProformaLine[] = [];
  if (currencies) {
    proformaLines.forEach((line) => {
      result.push(
        calculateProformaLine({
          currencies,
          localCurrency: line.CURRENCY_CODE,
          proformaInfo,
          multiplier: line.MULTIPLE,
          proformaLine: line,
          quantity: line.QUANTITY_IN,
          rebateAmount: line.REBATE_AMOUNT,
          rebatePercent: line.REBATE_IN,
          unitPrice: line.RATE,
          vatPercent: line.VAT_PERCENT,
        })
      );
    });
  }
  return result;
}

export function getSubtotalInLocalCurrency(
  proformaLines: ProformaLine[],
  localCurrency: string | undefined,
  currencies: ProformaCurrency[]
): number {
  let total = 0;
  proformaLines.forEach((line) => {
    const subtotal = getValueInLocalCurrency(
      getSubTotalValue(line.QUANTITY_IN ?? 0, line.RATE ?? 0, line.MULTIPLE ?? 0) ?? 0,
      line.CURRENCY_CODE,
      localCurrency,
      currencies
    );
    total += subtotal;
  });
  return total;
}

export function getProformaTotalAmountInLocalCurrency(proformaLines: ProformaLine[]): number {
  let total = 0;
  proformaLines.forEach((line) => {
    total += line.AMOUNT_IN ?? 0;
  });
  return total;
}

export function getTotalRebateInLocalCurrency(
  proformaLines: ProformaLine[],
  localCurrency: string | undefined,
  currencies: ProformaCurrency[]
): number {
  let total = 0;
  proformaLines.forEach((line) => {
    const rebate = getValueInLocalCurrency(line.REBATE_AMOUNT ?? 0, line.CURRENCY_CODE, localCurrency, currencies);
    total += rebate;
  });
  return total;
}

export function getTotalVatInLocalCurrency(
  proformaLines: ProformaLine[],
  localCurrency: string | undefined,
  currencies: ProformaCurrency[]
): number {
  let total = 0;
  proformaLines.forEach((line) => {
    const vat = getValueInLocalCurrency(line.VAT ?? 0, line.CURRENCY_CODE, localCurrency, currencies);
    total += vat;
  });
  return total;
}

export function reCalculateExhcangeRateSum(
  currencies: ProformaCurrency[],
  proformaLines: ProformaLine[],
  proformaInfo: ProformaInfo
) {
  // We must use the filter in order to get the correct order of the currencies
  // because the currencies are sorted by the PER_CURRENCY_NAME in the database and we need to keep this order.
  const result = currencies.map((currency) => {
    const { CUR_CODE1, CUR_CODE2, CUR_CODE3, CUR_CODE4 } = proformaInfo;
    let field = '';
    if (CUR_CODE1 === currency.CURRENCY_NAME) {
      field = `CUR1`;
    }
    if (CUR_CODE2 === currency.CURRENCY_NAME) {
      field = `CUR2`;
    }
    if (CUR_CODE3 === currency.CURRENCY_NAME) {
      field = `CUR3`;
    }
    if (CUR_CODE4 === currency.CURRENCY_NAME) {
      field = `CUR4`;
    }
    let sum = 0;
    proformaLines.forEach((line) => {
      if (field === 'CUR1' || field === 'CUR2' || field === 'CUR3' || field === 'CUR4') {
        const value = line[field] || 0;
        sum += value;
      }
    });
    return {
      ...currency,
      // Rounding problem with the sum due to the recalculation of the proforma lines
      SUM: parseFloat(sum.toFixed(2)),
    };
  });
  return result;
}

/** Finds the currency codes that are in use and updates the ProformaInfo object and returns the updated object. No direct mutations. */
export function updateProformaCurrecyCodes(currencies: ProformaCurrency[], proformaInfo: ProformaInfo) {
  const proformaDetails = { ...proformaInfo };
  const isUsed: string[] = currencies.filter((i) => i.PER_SHOW === 1).map((i) => i.CURRENCY_NAME);
  for (let index = 0; index < 4; index++) {
    const currencyCode = isUsed[index];
    const proformaCurrencyField = `CUR_CODE${index + 1}`;
    if (
      proformaCurrencyField === 'CUR_CODE1' ||
      proformaCurrencyField === 'CUR_CODE2' ||
      proformaCurrencyField === 'CUR_CODE3' ||
      proformaCurrencyField === 'CUR_CODE4'
    ) {
      proformaDetails[proformaCurrencyField] = currencyCode;
    }
  }
  return proformaDetails;
}

export function recalculateProforma(
  proformaLines: ProformaLine[],
  currencies: ProformaCurrency[],
  proformaInfo: ProformaInfo
): ProformaWindow {
  const proformaInfoResult = updateProformaCurrecyCodes(currencies, proformaInfo);
  const proformaLinesResult = proformaLines.map((proformaLine) => {
    const { QUANTITY_IN, RATE, MULTIPLE, REBATE_AMOUNT, REBATE_IN, VAT_PERCENT, VAT, CURRENCY_CODE } = proformaLine;
    const rebateAmount: ProformaLine['REBATE_AMOUNT'] = getRebateAmountFromRebatePercent(
      QUANTITY_IN,
      RATE,
      MULTIPLE,
      REBATE_AMOUNT,
      REBATE_IN
    );
    let vatAmount: ProformaLine['VAT'] = VAT;
    if (VAT_PERCENT) {
      vatAmount = getVatAmount(QUANTITY_IN, RATE, MULTIPLE, VAT_PERCENT, rebateAmount);
    }

    const baseAmount = (RATE ?? 0) * (QUANTITY_IN ?? 0) * (MULTIPLE ?? 1) + (rebateAmount ?? 0) + (vatAmount ?? 0);

    const amountIn = getValueInLocalCurrency(baseAmount, CURRENCY_CODE, proformaInfoResult.LOCAL_CURRENCY, currencies);

    const { CUR1, CUR2, CUR3, CUR4 } = updateProformaLineCurrencyField({
      currencies,
      proformaInfo: proformaInfoResult,
      proformaLineBaseAmount: baseAmount,
      proformaLineCurrencyCode: CURRENCY_CODE,
    });

    const result: ProformaLine = {
      ...proformaLine,
      REBATE_AMOUNT: rebateAmount,
      VAT: vatAmount,
      AMOUNT_IN: amountIn,
      BASE_AMOUNT: baseAmount,
      CUR1,
      CUR2,
      CUR3,
      CUR4,
    };

    return result;
  });

  const currenciesResult = reCalculateExhcangeRateSum(currencies, proformaLinesResult, proformaInfoResult);
  proformaInfoResult.SUBTOTAL_IN = getProformaTotalAmountInLocalCurrency(proformaLinesResult);
  return { proformaLines: proformaLinesResult, proformaInfo: proformaInfoResult, currencies: currenciesResult };
}
